import { html } from '../core/html.js';
import { t } from '../core/i18n.js';
import { routeHref } from '../core/url.js';
import { page, breadcrumbs } from '../components/chrome.js';
import { productCard } from '../components/productCard.js';
import { artwork } from '../components/artwork.js';
import { ctaSection } from '../components/ctaSection.js';
import { icon } from '../components/icon.js';
import { games, releasedGames, upcomingGames } from '../data/games.js';

const pad = (value) => String(value).padStart(2, '0');
const platforms = [...new Set(games.flatMap((game) => game.platforms))];
const spotlightGame = upcomingGames[0] ?? games[0];

const genres = [...new Set(releasedGames.map((game) => game.genreKey))].map((key) => ({
  key,
  slug: key.split('.').pop(),
  count: releasedGames.filter((game) => game.genreKey === key).length,
}));

const pageHead = () => html`
  <section class="pagehead" aria-labelledby="games-title">
    <div class="shell">
      ${breadcrumbs('nav.games')}
      <div class="pagehead__grid">
        <div>
          <p class="kicker">${t('games.label')}</p>
          <h1 class="pagehead__title" id="games-title">${t('games.title')}</h1>
          <p class="pagehead__lede">${t('games.lede')}</p>
        </div>
        <aside class="pagehead__aside">
          <p class="pagehead__note">
            <strong>${pad(releasedGames.length)}</strong>${t('home.stats.titles')}
          </p>
          <p class="pagehead__note">
            <strong>${pad(upcomingGames.length)}</strong>${t('home.stats.upcoming')}
          </p>
          <p class="pagehead__meta">${platforms.join(' · ')}</p>
        </aside>
      </div>
      <div class="cover-strip" aria-hidden="true">
        ${games.map(
          (game) => html`
            <span class="cover-strip__item">
              ${artwork(game.art, '', { sizes: '144px' })}
            </span>
          `,
        )}
      </div>
    </div>
  </section>
`;

const spotlight = () => {
  const title = t(`game.${spotlightGame.slug}.title`);

  return html`
    <section
      class="spotlight"
      style="--accent: ${spotlightGame.accent}"
      aria-labelledby="spotlight-title"
    >
      <div class="shell spotlight__grid">
        <div>
          <div class="spotlight__status-line">
            <p class="spotlight__status">${t('games.upcoming.label')}</p>
            <p class="spotlight__note">${t('games.upcoming.note')}</p>
          </div>
          <h2 class="spotlight__title" id="spotlight-title">${title}</h2>
          <p class="spotlight__meta">
            ${t(spotlightGame.genreKey)} · ${spotlightGame.platforms.join(' · ')}
          </p>
          <p class="spotlight__text">${t(`game.${spotlightGame.slug}.description`)}</p>
          <ul class="tag-list">
            ${spotlightGame.tagKeys.map((key) => html`<li>${t(key)}</li>`)}
          </ul>
          <div class="spotlight__actions">
            <a class="btn btn--paper btn--lg" href="${routeHref('/contact')}">
              ${t('games.upcoming.cta')}${icon('arrowRight', { size: 17 })}
            </a>
          </div>
        </div>
        <div class="spotlight__art">
          ${artwork(spotlightGame.art, title, {
            sizes: '(max-width: 919px) 92vw, 46vw',
            priority: true,
          })}
        </div>
      </div>
    </section>
  `;
};

const library = () => html`
  <section class="section" id="katalog" aria-labelledby="library-title">
    <div class="shell">
      <div class="section-label">
        <span class="section-label__num">02</span>
        <span>${t('games.library.label')}</span>
      </div>
      <div class="section-head">
        <h2 class="section-head__title" id="library-title">${t('games.library.title')}</h2>
        <p class="section-head__lede">${t('games.library.lede')}</p>
      </div>

      <div data-catalogue>
        <div class="catalogue__bar">
          <div class="filters" role="group" aria-label="${t('games.filter.label')}">
            <button class="filter" type="button" data-filter="all" aria-pressed="true">
              ${t('games.filter.all')}
              <span class="filter__count">${releasedGames.length}</span>
            </button>
            ${genres.map(
              (genre) => html`
                <button class="filter" type="button" data-filter="${genre.slug}" aria-pressed="false">
                  ${t(genre.key)}
                  <span class="filter__count">${genre.count}</span>
                </button>
              `,
            )}
          </div>
          <p class="catalogue__count" data-filter-status aria-live="polite">
            ${t('games.filter.count', { count: releasedGames.length })}
          </p>
        </div>

        <div class="game-grid">
          ${releasedGames.map((game, index) =>
            productCard(game, {
              namespace: 'game',
              index,
              media: artwork(game.art, t(`game.${game.slug}.title`), {
                sizes: '(max-width: 639px) 100vw, (max-width: 1079px) 48vw, 31vw',
              }),
            }),
          )}
        </div>
      </div>
    </div>
  </section>
`;

export const GamesPage = () => page(html`
  ${pageHead()}${spotlight()}${library()}
  ${ctaSection({
    labelKey: 'games.cta.label',
    titleKey: 'games.cta.title',
    ledeKey: 'games.cta.lede',
    buttonKey: 'games.cta.button',
  })}
`);
